import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Package, Hammer, ArrowDownCircle, ArrowUpCircle, MinusCircle,
  Plus, ClipboardList, ChevronLeft, ChevronRight, Info, ExternalLink, X, Loader2
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { Dialog, Modal } from '@components/Dialog';
import { PendingOrdersModal } from '@components/PendingOrdersModal';
import { productService } from '../../services/productService';
import { rawMaterialService } from '../../services/rawMaterialService';
import { stockMovementService } from '../../services/stockMovementService';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { materialReceiptService } from '../../services/materialReceiptService';
import { salesOrderService } from '../../services/salesOrderService';
import { Product } from '../../types/product';
import { RawMaterial } from '../../types/rawMaterial';
import { PurchaseOrder } from '../../types/purchaseOrder';
import { MaterialReceipt } from '../../types/materialReceipt';
import { SalesOrder } from '../../types/salesOrder';
import {
  StockMovement,
  StockMovementFormData,
  StockItemType,
  OriginType,
  MovementType,
  STOCK_ITEM_TYPE_OPTIONS,
  MANUAL_ORIGIN_TYPE_OPTIONS,
  MOVEMENT_TYPE_BY_ORIGIN,
} from '../../types/stockMovement';
import { clsx } from 'clsx';

// ─── Sub-components ───────────────────────────────────────────────────────────

const MovementTypeBadge: React.FC<{ type: number }> = ({ type }) => {
  const config: Record<number, { label: string; className: string; icon: React.ReactNode }> = {
    [MovementType.Entrada]: {
      label: 'Entrada',
      className: 'bg-green-100 text-green-700 border-green-200',
      icon: <ArrowDownCircle size={14} />,
    },
    [MovementType.Saida]: {
      label: 'Saída',
      className: 'bg-red-100 text-red-700 border-red-200',
      icon: <ArrowUpCircle size={14} />,
    },
    [MovementType.Saldo]: {
      label: 'Saldo',
      className: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: <MinusCircle size={14} />,
    },
  };
  const c = config[type] || { label: 'Desconhecido', className: 'bg-slate-100 text-slate-600', icon: null };
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border', c.className)}>
      {c.icon} {c.label}
    </span>
  );
};

const OriginTypeBadge: React.FC<{ type: number }> = ({ type }) => {
  const config: Record<number, { label: string; className: string }> = {
    [OriginType.PedidoCompra]: { label: 'Pedido de Compra', className: 'bg-purple-100 text-purple-700 border-purple-200' },
    [OriginType.PedidoVenda]: { label: 'Pedido de Venda', className: 'bg-orange-100 text-orange-700 border-orange-200' },
    [OriginType.AcertoManual]: { label: 'Acerto Manual', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    [OriginType.Balanco]: { label: 'Balanço', className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    [OriginType.RecebimentoMaterial]: { label: 'Recebimento Material', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  };
  const c = config[type] || { label: 'Desconhecido', className: 'bg-slate-100 text-slate-600' };
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border', c.className)}>
      {c.label}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const StockMovementListView: React.FC = () => {
  // Search / item type
  const [itemType, setItemType] = useState<StockItemType>(StockItemType.Produto);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<(Product | RawMaterial)[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Selected item
  const [selectedItem, setSelectedItem] = useState<Product | RawMaterial | null>(null);
  const [pendingOrdersModalOpen, setPendingOrdersModalOpen] = useState(false);

  // Movements table
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // New movement dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<StockMovementFormData>({
    origin_type: OriginType.AcertoManual,
    movement_type: MovementType.Entrada,
    quantity: 0,
    product_id: undefined,
    raw_material_id: undefined,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [lastPurchasePrice, setLastPurchasePrice] = useState<number | null>(null);
  const [lastPurchasePriceLoading, setLastPurchasePriceLoading] = useState(false);

  // Detail modals
  const [poDetailOpen, setPoDetailOpen] = useState(false);
  const [poDetail, setPoDetail] = useState<PurchaseOrder | null>(null);
  const [poDetailLoading, setPoDetailLoading] = useState(false);

  const [receiptDetailOpen, setReceiptDetailOpen] = useState(false);
  const [receiptDetail, setReceiptDetail] = useState<MaterialReceipt | null>(null);
  const [receiptDetailLoading, setReceiptDetailLoading] = useState(false);

  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [orderDetail, setOrderDetail] = useState<SalesOrder | null>(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);

  // ─── Search logic ───────────────────────────────────────────────────────

  const doSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    try {
      if (itemType === StockItemType.Produto) {
        const result = await productService.searchProducts(query, 1, 10);
        setSearchResults(result.products || []);
      } else {
        const result = await rawMaterialService.getRawMaterials({
          page: 1,
          pageSize: 10,
          searchName: query,
          searchCode: query,
        });
        setSearchResults(result.data || []);
      }
      setSearchOpen(true);
    } catch (err) {
      console.error('Erro na busca:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [itemType]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => doSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search, doSearch]);

  // ─── Load movements ────────────────────────────────────────────────────

  const loadMovements = useCallback(async () => {
    if (!selectedItem) return;
    setMovementsLoading(true);
    try {
      const searchModel = { page: currentPage, pageSize: pageSize };
      const result =
        itemType === StockItemType.Produto
          ? await stockMovementService.getMovementsByProduct((selectedItem as Product).id, searchModel)
          : await stockMovementService.getMovementsByRawMaterial((selectedItem as RawMaterial).id, searchModel);

      setMovements(result.data || []);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalItems(result.pagination?.total || 0);
    } catch (err) {
      console.error('Erro ao carregar lançamentos:', err);
    } finally {
      setMovementsLoading(false);
    }
  }, [selectedItem, currentPage, itemType]);

  useEffect(() => {
    if (selectedItem) loadMovements();
  }, [selectedItem, currentPage, loadMovements]);

  // ─── Select item ───────────────────────────────────────────────────────

  const handleSelectItem = (item: Product | RawMaterial) => {
    setSelectedItem(item);
    setSearch('');
    setSearchOpen(false);
    setCurrentPage(1);
    setMovements([]);
  };

  const handleChangeItemType = (type: StockItemType) => {
    setItemType(type);
    setSelectedItem(null);
    setSearch('');
    setSearchResults([]);
    setSearchOpen(false);
    setMovements([]);
    setCurrentPage(1);
  };

  // ─── New movement form ─────────────────────────────────────────────────

  const openNewMovementDialog = () => {
    if (!selectedItem) return;
    const isProduct = itemType === StockItemType.Produto;
    setFormData({
      origin_type: OriginType.AcertoManual,
      movement_type: MovementType.Entrada,
      quantity: 0,
      product_id: isProduct ? (selectedItem as Product).id : undefined,
      raw_material_id: !isProduct ? (selectedItem as RawMaterial).id : undefined,
    });
    setFormErrors({});
    setLastPurchasePrice(null);
    setDialogOpen(true);

    // Fetch last purchase price suggestion
    setLastPurchasePriceLoading(true);
    const fetchPrice = isProduct
      ? stockMovementService.getLastPurchasePriceByProduct((selectedItem as Product).id)
      : stockMovementService.getLastPurchasePriceByRawMaterial((selectedItem as RawMaterial).id);
    fetchPrice
      .then((price) => setLastPurchasePrice(price))
      .catch((err) => console.error('Erro ao buscar último preço de compra:', err))
      .finally(() => setLastPurchasePriceLoading(false));
  };

  const handleOriginChange = (originType: number) => {
    const availableMovementTypes = MOVEMENT_TYPE_BY_ORIGIN[originType] || [];
    setFormData(prev => ({
      ...prev,
      origin_type: originType,
      movement_type: availableMovementTypes.length > 0 ? availableMovementTypes[0].value : MovementType.Entrada,
    }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (formData.quantity <= 0) {
      errors.quantity = 'Quantidade deve ser maior que zero';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await stockMovementService.createMovement(formData);
      setDialogOpen(false);
      setSuccessMessage('Lançamento de estoque criado com sucesso!');
      setTimeout(() => setSuccessMessage(''), 4000);
      loadMovements();
      // Refresh selected item data so the stock panel reflects updated values
      if (selectedItem) {
        try {
          if (itemType === StockItemType.Produto) {
            const updated = await productService.getProductById((selectedItem as Product).id);
            setSelectedItem(updated);
          } else {
            const updated = await rawMaterialService.getRawMaterialById((selectedItem as RawMaterial).id);
            setSelectedItem(updated);
          }
        } catch {
          // Non-critical: panel will show stale data but movements list is refreshed
        }
      }
    } catch (err) {
      console.error('Erro ao criar lançamento:', err);
      setFormErrors({ submit: 'Falha ao criar lançamento. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Helpers ────────────────────────────────────────────────────────────

  const isProduct = (item: Product | RawMaterial): item is Product => 'sku' in item;

  const getItemLabel = (item: Product | RawMaterial) => {
    if (isProduct(item)) return `${item.sku} - ${item.description}`;
    return `${item.code} - ${item.description}`;
  };

  const getItemId = (item: Product | RawMaterial) => {
    if (isProduct(item)) return `SKU: ${item.sku}`;
    return `Código: ${item.code}`;
  };

  const getItemStock = (item: Product | RawMaterial) => item.stock_quantity ?? 0;
  const getItemStockAvailable = (item: Product | RawMaterial) => item.stock_quantity_available ?? 0;
  const getItemCost = (item: Product | RawMaterial) => item.cost;

  // ─── Detail modal handlers ─────────────────────────────────────────

  const openPurchaseOrderDetail = async (poId: number) => {
    setPoDetailOpen(true);
    setPoDetailLoading(true);
    try {
      const data = await purchaseOrderService.getPurchaseOrderById(poId);
      setPoDetail(data);
    } catch (err) {
      console.error('Erro ao carregar pedido de compra:', err);
    } finally {
      setPoDetailLoading(false);
    }
  };

  const openMaterialReceiptDetail = async (receiptId: number) => {
    setReceiptDetailOpen(true);
    setReceiptDetailLoading(true);
    try {
      const data = await materialReceiptService.getMaterialReceiptById(receiptId);
      setReceiptDetail(data);
    } catch (err) {
      console.error('Erro ao carregar recebimento de material:', err);
    } finally {
      setReceiptDetailLoading(false);
    }
  };

  const openSalesOrderDetail = async (orderId: number) => {
    setOrderDetailOpen(true);
    setOrderDetailLoading(true);
    try {
      const data = await salesOrderService.getOrderById(orderId);
      setOrderDetail(data);
    } catch (err) {
      console.error('Erro ao carregar pedido de venda:', err);
    } finally {
      setOrderDetailLoading(false);
    }
  };

  const renderOriginLinks = (mov: StockMovement) => {
    const links: React.ReactNode[] = [];

    // Link para pedido de venda (sempre que order_id estiver disponível)
    if (mov.order_id) {
      links.push(
        <button
          key="order"
          className="text-orange-600 hover:text-orange-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => openSalesOrderDetail(mov.order_id!)}
        >
          <ExternalLink size={12} /> Pedido #{mov.order_id}
        </button>
      );
    }

    // Link para recebimento de material (sempre que material_receipt_id estiver disponível)
    if (mov.material_receipt_id) {
      links.push(
        <button
          key="receipt"
          className="text-indigo-600 hover:text-indigo-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => openMaterialReceiptDetail(mov.material_receipt_id!)}
        >
          <ExternalLink size={12} /> Receb. #{mov.material_receipt_id}
        </button>
      );
    }

    // Link para pedido de compra (sempre que purchase_order_id estiver disponível)
    if (mov.purchase_order_id) {
      links.push(
        <button
          key="po"
          className="text-purple-600 hover:text-purple-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => openPurchaseOrderDetail(mov.purchase_order_id!)}
        >
          <ExternalLink size={12} /> PC #{mov.purchase_order_number || mov.purchase_order_id}
        </button>
      );
    }

    // Fallback: referência de origem quando não há links específicos
    if (links.length === 0 && mov.origin_reference) {
      links.push(
        <span key="ref" className="text-xs text-slate-500">Ref: {mov.origin_reference}</span>
      );
    }

    if (links.length === 0) return <span className="text-xs text-slate-400">—</span>;
    return <div className="flex flex-col gap-1">{links}</div>;
  };

  const formatCurrency = (value?: number | null) => {
    if (value === undefined || value === null) return '—';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatQty = (value?: number | null) => {
    if (value === undefined || value === null) return '—';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  // Available movement types for selected origin
  const availableMovementTypes = MOVEMENT_TYPE_BY_ORIGIN[formData.origin_type] || [];

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-h1">Conferência de Estoque</h1>
          <p className="text-sm text-slate-500 mt-1">Visualize e registre lançamentos de estoque de produtos e matérias-primas</p>
        </div>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Info size={18} />
          {successMessage}
        </div>
      )}

      {/* Search section */}
      <Card>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Item type selector */}
            <div className="w-full sm:w-48">
              <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de item</label>
              <select
                value={itemType}
                onChange={(e) => handleChangeItemType(Number(e.target.value) as StockItemType)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {STOCK_ITEM_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Search input */}
            <div className="flex-1 relative">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {itemType === StockItemType.Produto ? 'Buscar produto por SKU ou nome' : 'Buscar matéria-prima por código ou nome'}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                  placeholder={
                    itemType === StockItemType.Produto
                      ? 'Digite o SKU ou nome do produto...'
                      : 'Digite o código ou nome da matéria-prima...'
                  }
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              {/* Dropdown results */}
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
                  <ul className="py-1">
                    {searchResults.map((item) => (
                      <li
                        key={'sku' in item ? `p-${item.id}` : `rm-${item.id}`}
                        onMouseDown={() => handleSelectItem(item)}
                        className="px-4 py-2 cursor-pointer hover:bg-slate-50 flex items-center gap-3"
                      >
                        {isProduct(item) ? (
                          <Package size={16} className="text-slate-400 flex-shrink-0" />
                        ) : (
                          <Hammer size={16} className="text-slate-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-900">
                              {isProduct(item) ? item.sku : (item as RawMaterial).code}
                            </span>
                            <span className="text-xs text-slate-400">|</span>
                            <span className="text-sm text-slate-600 truncate">{item.description}</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            Estoque: {formatQty(item.stock_quantity)} | Disponível: {formatQty(item.stock_quantity_available)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {searchOpen && search.trim() && searchResults.length === 0 && !searchLoading && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg">
                  <div className="px-4 py-3 text-sm text-slate-500">Nenhum resultado encontrado</div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected item info */}
      {selectedItem && (
        <Card>
          <CardContent>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex items-start gap-3">
                <div className={clsx(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  itemType === StockItemType.Produto ? 'bg-emerald-100' : 'bg-amber-100'
                )}>
                  {itemType === StockItemType.Produto
                    ? <Package size={20} className="text-emerald-600" />
                    : <Hammer size={20} className="text-amber-600" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{getItemLabel(selectedItem)}</h3>
                  <p className="text-sm text-slate-500">{getItemId(selectedItem)}</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-xs text-slate-500">Estoque Físico</p>
                  <p className="text-lg font-bold text-slate-900">{formatQty(getItemStock(selectedItem))}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500">Estoque Disponível</p>
                  <p className="text-lg font-bold text-slate-900">{formatQty(getItemStockAvailable(selectedItem))}</p>
                  {itemType === StockItemType.Produto && isProduct(selectedItem) && (
                    <button
                      className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-medium mt-0.5"
                      onClick={() => setPendingOrdersModalOpen(true)}
                    >
                      Ver reservas
                    </button>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500">Custo</p>
                  <p className="text-lg font-bold text-slate-900">{formatCurrency(getItemCost(selectedItem))}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Movements section */}
      {selectedItem && (
        <Card>
          <div className="p-4 border-b border-slate-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <ClipboardList size={20} className="text-slate-600" />
                <h2 className="text-lg font-semibold text-slate-900">Lançamentos de Estoque</h2>
                <span className="text-sm text-slate-500">({totalItems} registros)</span>
              </div>
              <PermissionGuard permission="stock_movements.create">
                <Button icon={<Plus size={18} />} onClick={openNewMovementDialog} size="sm">
                  Novo Lançamento
                </Button>
              </PermissionGuard>
            </div>
          </div>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <th className="text-left">Data</th>
                    <th className="text-left">Origem</th>
                    <th className="text-left">Referência</th>
                    <th className="text-center">Tipo</th>
                    <th className="text-right">Quantidade</th>
                    <th className="text-right">Saldo Anterior</th>
                    <th className="text-right">Novo Saldo</th>
                    <th className="text-right">Preço Compra</th>
                    <th className="text-right">Preço Custo</th>
                    <th className="text-left">Observação</th>
                    <th className="text-left">Usuário</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {movementsLoading && (
                    <tr>
                      <td colSpan={11} className="text-center py-12">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-slate-500">Carregando lançamentos...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!movementsLoading && movements.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-12">
                        <div className="flex flex-col items-center">
                          <div className="text-5xl text-slate-200 mb-3">📦</div>
                          <h3 className="text-base font-medium text-slate-700 mb-1">Nenhum lançamento encontrado</h3>
                          <p className="text-sm text-slate-500">Não há lançamentos de estoque registrados para este item</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!movementsLoading && movements.map((mov) => (
                    <TableRow key={mov.id}>
                      <TableCell className="whitespace-nowrap">{formatDate(mov.movement_date)}</TableCell>
                      <TableCell><OriginTypeBadge type={mov.origin_type} /></TableCell>
                      <TableCell>{renderOriginLinks(mov)}</TableCell>
                      <TableCell className="text-center"><MovementTypeBadge type={mov.movement_type} /></TableCell>
                      <TableCell className="text-right font-medium">{formatQty(mov.quantity)}</TableCell>
                      <TableCell className="text-right">{formatQty(mov.previous_balance)}</TableCell>
                      <TableCell className="text-right">{formatQty(mov.new_balance)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(mov.purchase_price)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(mov.cost_price)}</TableCell>
                      <TableCell className="max-w-xs truncate">{mov.notes || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-500 whitespace-nowrap">{mov.created_by}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Página {currentPage} de {totalPages} ({totalItems} registros)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    icon={<ChevronLeft size={16} />}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    icon={<ChevronRight size={16} />}
                    iconPosition="right"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Placeholder when no item selected */}
      {!selectedItem && (
        <Card>
          <CardContent>
            <div className="text-center py-16">
              <div className="text-6xl text-slate-200 mb-4">🔍</div>
              <h3 className="text-lg font-medium text-slate-700 mb-2">Selecione um item para visualizar os lançamentos</h3>
              <p className="text-sm text-slate-500">
                Use a barra de pesquisa acima para buscar um produto ou matéria-prima e visualizar seu histórico de movimentações de estoque.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── New Movement Dialog ───────────────────────────────────────── */}
      <Dialog
        isOpen={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        title="Novo Lançamento de Estoque"
        size="lg"
        closeOnBackdrop={!saving}
        showCloseButton={!saving}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Salvar Lançamento
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Item info */}
          {selectedItem && (
            <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
              {itemType === StockItemType.Produto
                ? <Package size={18} className="text-emerald-600" />
                : <Hammer size={18} className="text-amber-600" />}
              <div>
                <p className="font-medium text-sm text-slate-900">{getItemLabel(selectedItem)}</p>
                <p className="text-xs text-slate-500">
                  Estoque atual: {formatQty(getItemStock(selectedItem))} | Disponível: {formatQty(getItemStockAvailable(selectedItem))}
                </p>
              </div>
            </div>
          )}

          {/* Origin type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Tipo de Origem <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.origin_type}
              onChange={(e) => handleOriginChange(Number(e.target.value))}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              disabled={saving}
            >
              {MANUAL_ORIGIN_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              {formData.origin_type === OriginType.AcertoManual
                ? 'Acerto manual de estoque — acrescenta ou diminui o saldo físico e virtual do item conforme a quantidade registrada'
                : 'Balanço / Inventário — ajusta o saldo físico e virtual do item para o valor registrado'}
            </p>
          </div>

          {/* Movement type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Tipo de Movimentação <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.movement_type}
              onChange={(e) => setFormData(prev => ({ ...prev, movement_type: Number(e.target.value) }))}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              disabled={saving || availableMovementTypes.length <= 1}
            >
              {availableMovementTypes.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {formData.origin_type === OriginType.Balanco && (
              <p className="text-xs text-slate-400 mt-1">
                Para balanço, o tipo é sempre "Saldo" — a quantidade informada será o novo saldo do item
              </p>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {formData.origin_type === OriginType.Balanco ? 'Saldo Atual (Quantidade)' : 'Quantidade'} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.0001"
              min="0"
              value={formData.quantity || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
              className={clsx(
                'w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500',
                formErrors.quantity ? 'border-red-300' : 'border-slate-300'
              )}
              disabled={saving}
              placeholder={formData.origin_type === OriginType.Balanco ? 'Informe o saldo real do item' : 'Informe a quantidade'}
            />
            {formErrors.quantity && <p className="text-xs text-red-500 mt-1">{formErrors.quantity}</p>}
          </div>

          {/* Purchase price */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Preço de Compra (Fornecedor)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.purchase_price ?? ''}
              onChange={(e) => setFormData(prev => ({ ...prev, purchase_price: e.target.value ? parseFloat(e.target.value) : undefined }))}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              disabled={saving}
              placeholder="Opcional — preço de compra do fornecedor"
            />
            <p className="text-xs text-slate-400 mt-1">Campo opcional. Registro do preço pago ao fornecedor.</p>
          </div>

          {/* Cost price */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Preço de Custo
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.cost_price ?? ''}
                onChange={(e) => setFormData(prev => ({ ...prev, cost_price: e.target.value ? parseFloat(e.target.value) : undefined }))}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                disabled={saving}
                placeholder="Opcional — se vazio, usa o custo atual do item"
              />
              {lastPurchasePriceLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            {lastPurchasePrice !== null && !lastPurchasePriceLoading && (
              <div className="mt-1 flex items-center gap-1">
                <span className="text-xs text-slate-500">
                  Último preço de compra: {formatCurrency(lastPurchasePrice)}
                </span>
                <button
                  type="button"
                  className="text-xs text-emerald-600 hover:text-emerald-800 font-medium hover:underline"
                  onClick={() => setFormData(prev => ({ ...prev, cost_price: lastPurchasePrice }))}
                  disabled={saving}
                >
                  Usar este valor
                </button>
              </div>
            )}
            {lastPurchasePrice === null && !lastPurchasePriceLoading && (
              <p className="text-xs text-slate-400 mt-1">
                Se não informado, o sistema usará o custo atual cadastrado no item.
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Observação</label>
            <textarea
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value || undefined }))}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              disabled={saving}
              placeholder="Descreva o motivo do lançamento..."
            />
          </div>

          {/* Submit error */}
          {formErrors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {formErrors.submit}
            </div>
          )}

          {/* Business rules info */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <p className="font-medium mb-1">⚠️ Atenção:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Lançamentos manuais afetam diretamente o saldo em estoque (físico e virtual) do item</li>
              <li>Não é possível alterar ou excluir lançamentos após a criação</li>
              {formData.origin_type === OriginType.AcertoManual && (
                <li>Acerto manual: a quantidade informada será somada (entrada) ou subtraída (saída) do saldo atual</li>
              )}
              {formData.origin_type === OriginType.Balanco && (
                <>
                  <li>Balanço: a quantidade informada será o novo saldo do item, substituindo o saldo atual</li>
                </>
              )}
              <li>Se o preço de custo não for informado, o sistema usará o custo atual do cadastro do item</li>
            </ul>
          </div>
        </div>
      </Dialog>

      {/* ─── Purchase Order Detail Modal ───────────────────────── */}
      <Modal isOpen={poDetailOpen} onClose={() => { setPoDetailOpen(false); setPoDetail(null); }} size="lg">
        <div className="relative">
          <button
            className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600"
            onClick={() => { setPoDetailOpen(false); setPoDetail(null); }}
          >
            <X size={16} />
          </button>
          {poDetailLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Carregando pedido de compra...</span>
            </div>
          )}
          {!poDetailLoading && poDetail && (
            <div className="space-y-4 p-2">
              <h2 className="text-lg font-bold text-slate-900">
                Pedido de Compra #{poDetail.order_number || poDetail.purchase_order_id}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-xs text-slate-500 block">Fornecedor</span>
                  <span className="font-medium">{poDetail.provider_name || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Data</span>
                  <span className="font-medium">{poDetail.order_date ? new Date(poDetail.order_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Status</span>
                  <span className="font-medium">
                    {poDetail.status === 0 ? 'Em Aberto' : poDetail.status === 1 ? 'Atendido' : poDetail.status === 2 ? 'Cancelado' : 'Em Andamento'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Total</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(poDetail.total)}</span>
                </div>
              </div>
              {poDetail.items && poDetail.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Itens ({poDetail.items.length})</h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase">
                          <th className="py-2 px-3 text-left">Descrição</th>
                          <th className="py-2 px-3 text-left">SKU</th>
                          <th className="py-2 px-3 text-right">Qtde</th>
                          <th className="py-2 px-3 text-right">Valor Unit.</th>
                          <th className="py-2 px-3 text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poDetail.items.map((item, idx) => (
                          <tr key={item.purchase_order_item_id || idx} className="border-b border-slate-100">
                            <td className="py-2 px-3">{item.description}</td>
                            <td className="py-2 px-3 text-slate-500">{item.sku || '—'}</td>
                            <td className="py-2 px-3 text-right">{item.quantity}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(item.unit_value)}</td>
                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(item.unit_value * item.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Material Receipt Detail Modal ────────────────────── */}
      <Modal isOpen={receiptDetailOpen} onClose={() => { setReceiptDetailOpen(false); setReceiptDetail(null); }} size="lg">
        <div className="relative">
          <button
            className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600"
            onClick={() => { setReceiptDetailOpen(false); setReceiptDetail(null); }}
          >
            <X size={16} />
          </button>
          {receiptDetailLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Carregando recebimento...</span>
            </div>
          )}
          {!receiptDetailLoading && receiptDetail && (
            <div className="space-y-4 p-2">
              <h2 className="text-lg font-bold text-slate-900">
                Recebimento de Material #{receiptDetail.material_receipt_id}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-xs text-slate-500 block">Fornecedor</span>
                  <span className="font-medium">{receiptDetail.provider_name || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Data Recebimento</span>
                  <span className="font-medium">{receiptDetail.receipt_date ? new Date(receiptDetail.receipt_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Status</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    receiptDetail.status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {receiptDetail.status === 1 ? 'Conferido' : 'Em conferência'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Transportadora</span>
                  <span className="font-medium">{receiptDetail.carrier || '—'}</span>
                </div>
              </div>
              {receiptDetail.items && receiptDetail.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Itens ({receiptDetail.items.length})</h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase">
                          <th className="py-2 px-3 text-left">Descrição</th>
                          <th className="py-2 px-3 text-right">Qtde</th>
                          <th className="py-2 px-3 text-right">Valor Unit.</th>
                          <th className="py-2 px-3 text-right">Subtotal</th>
                          <th className="py-2 px-3 text-left">Pedido Compra</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receiptDetail.items.map((item, idx) => (
                          <tr key={item.material_receipt_item_id || idx} className="border-b border-slate-100">
                            <td className="py-2 px-3">{item.description}</td>
                            <td className="py-2 px-3 text-right">{item.quantity}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(item.unit_value)}</td>
                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(item.unit_value * item.quantity)}</td>
                            <td className="py-2 px-3 text-slate-500">
                              {item.purchase_order_number
                                ? `#${item.purchase_order_number}`
                                : item.purchase_order_id
                                  ? `PC #${item.purchase_order_id}`
                                  : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Sales Order Detail Modal ─────────────────────────── */}
      <Modal isOpen={orderDetailOpen} onClose={() => { setOrderDetailOpen(false); setOrderDetail(null); }} size="lg">
        <div className="relative">
          <button
            className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600"
            onClick={() => { setOrderDetailOpen(false); setOrderDetail(null); }}
          >
            <X size={16} />
          </button>
          {orderDetailLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Carregando pedido de venda...</span>
            </div>
          )}
          {!orderDetailLoading && orderDetail && (
            <div className="space-y-4 p-2">
              <h2 className="text-lg font-bold text-slate-900">
                Pedido de Venda #{orderDetail.order_id}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-xs text-slate-500 block">Cliente</span>
                  <span className="font-medium">{orderDetail.customer_name || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Data</span>
                  <span className="font-medium">{orderDetail.order_date ? new Date(orderDetail.order_date.includes('T') ? orderDetail.order_date : orderDetail.order_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Status</span>
                  <span className="font-medium">{orderDetail.order_status || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Total</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(orderDetail.order_sell_total)}</span>
                </div>
              </div>
              {orderDetail.items && orderDetail.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Itens ({orderDetail.items.length})</h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase">
                          <th className="py-2 px-3 text-left">Descrição</th>
                          <th className="py-2 px-3 text-left">SKU</th>
                          <th className="py-2 px-3 text-right">Qtde</th>
                          <th className="py-2 px-3 text-right">Valor Unit.</th>
                          <th className="py-2 px-3 text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetail.items.map((item: any, idx: number) => (
                          <tr key={item.order_item_id || idx} className="border-b border-slate-100">
                            <td className="py-2 px-3">{item.description || '—'}</td>
                            <td className="py-2 px-3 text-slate-500">{item.sku || '—'}</td>
                            <td className="py-2 px-3 text-right">{item.quantity}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(item.unit_price)}</td>
                            <td className="py-2 px-3 text-right font-medium">{formatCurrency((item.unit_price || 0) * (item.quantity || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Pending Orders Modal ──────────────────────────────── */}
      {selectedItem && itemType === StockItemType.Produto && isProduct(selectedItem) && (
        <PendingOrdersModal
          isOpen={pendingOrdersModalOpen}
          onClose={() => setPendingOrdersModalOpen(false)}
          productId={(selectedItem as Product).id}
          productSku={(selectedItem as Product).sku}
          productDescription={(selectedItem as Product).description || undefined}
        />
      )}
    </div>
  );
};
